import React, { useState, useEffect, useRef, Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry';
import { STLExporter } from 'three/examples/jsm/exporters/STLExporter';
import { CSG } from 'three-csg-ts';
import robotoFont from '../../../fonts/Transformers Movie_Regular.json';
import ClipperLib from 'clipper-lib';

//Super Vanilla_Regular --
//Spooky Cartoon_Regular ++
//Kind Daily_Regular -
//Candy Beans_Regular -
//The Last Shuriken_Regular ++
//Transformers Movie_Regular +
//Zombie Zone_Regular ++
//Toasty Milk_Regular +


//-*Kraash Black_Regular
//-*Karate_Medium
//-*Simply_Rounded_Bold


//-Halo Dek_Regular
//-Sunday Midnight_Regular
//-Kraash_Regular
//-Billion Dreams_Regular
//-Nature Beauty Personal Use_Regular
//-Sealight Whisper_Regular

const font = new FontLoader().parse(robotoFont);

function crearLetraHollowMesh(letra, config) {
    const { size, height, thickness } = config;
    const shapes = font.generateShapes(letra, size, 2);
    const scale = 100;
  
    let geometries = [];
  
    for (const shape of shapes) {
      const outerPath = shape.getPoints().map(p => ({ X: p.x * scale, Y: p.y * scale }));
      const co = new ClipperLib.ClipperOffset();
      co.AddPath(outerPath, ClipperLib.JoinType.jtRound, ClipperLib.EndType.etClosedPolygon);
  
      if (shape.holes && shape.holes.length > 0) {
        for (const hole of shape.holes) {
          const holePath = hole.getPoints().map(p => ({ X: p.x * scale, Y: p.y * scale })).reverse();
          co.AddPath(holePath, ClipperLib.JoinType.jtRound, ClipperLib.EndType.etClosedPolygon);
        }
      }
  
      const offsetPaths = [];
      co.Execute(offsetPaths, -thickness * scale);
      if (offsetPaths.length < 1) continue;
  
      const offsetShape = new THREE.Shape();
      const [mainOffset, ...holeOffsets] = offsetPaths;
      const mainPath = mainOffset.map(p => new THREE.Vector2(p.X / scale, p.Y / scale));
      offsetShape.setFromPoints(mainPath);
      for (let holePath of holeOffsets) {
        const path = holePath.map(p => new THREE.Vector2(p.X / scale, p.Y / scale));
        offsetShape.holes.push(new THREE.Path(path));
      }
  
      const outerGeo = new THREE.ExtrudeGeometry(shape, { depth: height, bevelEnabled: false });
      const innerGeo = new THREE.ExtrudeGeometry(offsetShape, { depth: height - thickness, bevelEnabled: false });
  
      outerGeo.computeBoundingBox();
      const outerCenter = new THREE.Vector3();
      outerGeo.boundingBox.getCenter(outerCenter);
      outerGeo.translate(-outerCenter.x, -outerCenter.y, -outerCenter.z);
  
      innerGeo.computeBoundingBox();
      const innerCenter = new THREE.Vector3();
      innerGeo.boundingBox.getCenter(innerCenter);
      innerGeo.translate(-innerCenter.x, -innerCenter.y, -innerCenter.z);
      innerGeo.translate(0, 0, thickness / 2);
  
      const outerMesh = new THREE.Mesh(outerGeo);
      const innerMesh = new THREE.Mesh(innerGeo);
      let result = CSG.subtract(outerMesh, innerMesh);
  
      // 👉 Nuevo: Contornos visibles sobre agujeros
// 👉 Contornos visibles sobre agujeros internos (centrados correctamente)
if (shape.holes && shape.holes.length > 0) {
  for (let hole of shape.holes) {
    const holePts = hole.getPoints().map(p => ({ X: p.x * scale, Y: p.y * scale }));
    const coHoleOuter = new ClipperLib.ClipperOffset();
    coHoleOuter.AddPath(holePts.reverse(), ClipperLib.JoinType.jtRound, ClipperLib.EndType.etClosedPolygon);

    const outerHoleOffset = [];
    coHoleOuter.Execute(outerHoleOffset, thickness * scale);
    if (outerHoleOffset.length === 0) continue;

    const outerShape = new THREE.Shape(outerHoleOffset[0].map(p => new THREE.Vector2(p.X / scale, p.Y / scale)));

    const innerHolePath = holePts.map(p => new THREE.Vector2(p.X / scale, p.Y / scale));
    const pathHole = new THREE.Path(innerHolePath);
    outerShape.holes.push(pathHole);

    const geo = new THREE.ExtrudeGeometry(outerShape, {
        depth: height,
        bevelEnabled: false,
      });
      geo.translate(0, 0, -thickness / 2); // 💡 Igual al exterior
      

    // 👉 Usa el mismo centro de la letra completa (outerCenter), no el centro local del agujero
    geo.translate(-outerCenter.x, -outerCenter.y, -outerCenter.z);
    geo.translate(0, 0, thickness / 2);

    const ringMesh = new THREE.Mesh(geo);
    result = CSG.union(result, ringMesh); // 💡 Agrega contorno interno hueco centrado
  }
}

  
      geometries.push(result);
    }
  
    let combined = geometries[0];
    for (let i = 1; i < geometries.length; i++) {
      combined = CSG.union(combined, geometries[i]);
    }
  
    const coverGeo = new THREE.ExtrudeGeometry(shapes[0], {
      depth: 1,
      bevelEnabled: false
    });
    coverGeo.translate(0, 0, height);
  
    const coverMesh = new THREE.Mesh(coverGeo);
  
    const material = new THREE.MeshStandardMaterial({
      color: config.color,
      transparent: true,
      opacity: config.opacity,
      side: THREE.DoubleSide,
    });
  
    const visibleMesh = new THREE.Mesh(combined.geometry, material);
  
    return {
      visibleMesh,
      hollowMesh: combined,
      coverMesh,
    };
  }
  
function LetraPreview({ letra, config }) {
  const groupRef = useRef();

  useEffect(() => {
    const { visibleMesh } = crearLetraHollowMesh(letra, config);
    groupRef.current.clear();
    groupRef.current.add(visibleMesh);
  }, [letra, config]);

  return <group ref={groupRef} />;
}

function ExportarSTL({ letra, config }) {
  const handleExport = (tipo = 'base') => {
    const { hollowMesh, coverMesh } = crearLetraHollowMesh(letra, config);
    const mesh = tipo === 'tapa' ? coverMesh : hollowMesh;

    mesh.updateMatrixWorld(true);
    const exporter = new STLExporter();
    const stl = exporter.parse(mesh);

    const blob = new Blob([stl], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `letra_${letra}_${tipo}_${Date.now()}.stl`;
    a.click();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
      <button onClick={() => handleExport('base')}>📦 Exportar Base "{letra}"</button>
      <button onClick={() => handleExport('tapa')}>🧩 Exportar Tapa "{letra}"</button>
    </div>
  );
}

function crearNombreCompleto(nombre, config) {
  const letras = nombre.split('');
  const meshes = [];
  const tapas = [];

  let offsetX = 0;
  const overlap = 4; // mm de solapamiento deseado

  for (let i = 0; i < letras.length; i++) {
    const letra = letras[i];
    const { hollowMesh, coverMesh } = crearLetraHollowMesh(letra, config);

    // Obtener ancho antes de mover
    const bbox = new THREE.Box3().setFromObject(hollowMesh);
    const letraWidth = bbox.max.x - bbox.min.x;

    // Posicionar
    hollowMesh.position.x = offsetX - bbox.min.x;
    coverMesh.position.x = offsetX - bbox.min.x;

    hollowMesh.updateMatrixWorld();
    coverMesh.updateMatrixWorld();

    meshes.push(hollowMesh);
    tapas.push(coverMesh);

    // 👇 Aquí aplicamos solapamiento: restamos un poco
    offsetX += letraWidth - overlap;
  }

  // Unión de las letras y tapas
  let conjunto = meshes[0];
  for (let i = 1; i < meshes.length; i++) {
    conjunto = CSG.union(conjunto, meshes[i]);
  }

  let conjuntoTapa = tapas[0];
  for (let i = 1; i < tapas.length; i++) {
    conjuntoTapa = CSG.union(conjuntoTapa, tapas[i]);
  }

  return { conjunto, conjuntoTapa };
}





export default function GeneradorLampara3D() {
  const [nombre, setNombre] = useState('ELIAN');
  const [config, setConfig] = useState({
    size: 60,
    height: 15,
    thickness: 4,
    color: '#29A9FF',
    opacity: 0.8,
  });

  const [generarLetrasSeparadas, setGenerarLetrasSeparadas] = useState(false);
  const [generarNombreCompleto, setGenerarNombreCompleto] = useState(true);

  return (
    <div style={{ padding: 20, background: '#fff' }}>
      <h2>💡 Generador de Lámpara con Nombre</h2>

      {/* Entrada de texto */}
      <div style={{ margin: '10px 0' }}>
        <label>
          Nombre:{' '}
          <input
            value={nombre}
            onChange={(e) => setNombre(e.target.value.toUpperCase())}
          />
        </label>
      </div>

      {/* Configuración de tamaño, altura, etc */}
      <div>
        <label>
          Altura de letras:
          <input
            type="range"
            min={10}
            max={60}
            value={config.size}
            onChange={(e) =>
              setConfig((prev) => ({ ...prev, size: parseFloat(e.target.value) }))
            }
          />
          {config.size} mm
        </label>
      </div>

      <div>
        <label>
          Profundidad de letras:
          <input
            type="range"
            min={5}
            max={30}
            value={config.height}
            onChange={(e) =>
              setConfig((prev) => ({ ...prev, height: parseFloat(e.target.value) }))
            }
          />
          {config.height} mm
        </label>
      </div>

      <div>
        <label>
          Grosor del hueco:
          <input
            type="range"
            min={1}
            max={10}
            value={config.thickness}
            onChange={(e) =>
              setConfig((prev) => ({
                ...prev,
                thickness: parseFloat(e.target.value),
              }))
            }
          />
          {config.thickness} mm
        </label>
      </div>

      <div>
        <label>
          Color:
          <input
            type="color"
            value={config.color}
            onChange={(e) =>
              setConfig((prev) => ({ ...prev, color: e.target.value }))
            }
          />
        </label>
      </div>

      <div>
        <label>
          Transparencia:
          <input
            type="range"
            min={0.1}
            max={1}
            step={0.1}
            value={config.opacity}
            onChange={(e) =>
              setConfig((prev) => ({
                ...prev,
                opacity: parseFloat(e.target.value),
              }))
            }
          />
          {config.opacity}
        </label>
      </div>

      {/* Nuevos checkboxes */}
      <div style={{ margin: '15px 0', display: 'flex', flexDirection: 'column', gap: 5 }}>
        <label>
          <input
            type="checkbox"
            checked={generarLetrasSeparadas}
            onChange={(e) => setGenerarLetrasSeparadas(e.target.checked)}
          />
          Generar Letras Separadas
        </label>

        <label>
          <input
            type="checkbox"
            checked={generarNombreCompleto}
            onChange={(e) => setGenerarNombreCompleto(e.target.checked)}
          />
          Generar Nombre Completo
        </label>
      </div>

      {/* Render de letras separadas */}
      {generarLetrasSeparadas && (
        <>
          <h3>🧱 Letras en 3D</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
            {nombre.split('').map((letra, i) => (
              <div key={i} style={{ width: 150, height: 170 }}>
                <Canvas camera={{ position: [0, 0, 80], fov: 60 }}>
                  <ambientLight intensity={0.5} />
                  <directionalLight position={[10, 10, 10]} intensity={1} />
                  <Suspense fallback={null}>
                    <LetraPreview letra={letra} config={config} />
                  </Suspense>
                  <OrbitControls enableZoom enablePan enableRotate />
                </Canvas>
                <ExportarSTL letra={letra} config={config} />
              </div>
            ))}
          </div>
        </>
      )}

      {/* Render de nombre completo */}
      {generarNombreCompleto && (
        <>
          <h3>🧩 Nombre Completo en 3D</h3>
          <div style={{ width: '100%', height: 220 }}>
            <Canvas camera={{ position: [0, 0, 150], fov: 60 }}>
              <ambientLight intensity={0.5} />
              <directionalLight position={[10, 10, 10]} />
              <Suspense fallback={null}>
                {(() => {
                  const { conjunto } = crearNombreCompleto(nombre, config);
                  const visibleMesh = new THREE.Mesh(
                    conjunto.geometry,
                    new THREE.MeshStandardMaterial({
                      color: config.color,
                      transparent: true,
                      opacity: config.opacity,
                      side: THREE.DoubleSide,
                    })
                  );
                  return <primitive object={visibleMesh} />;
                })()}
              </Suspense>
              <OrbitControls enableZoom enablePan enableRotate />
            </Canvas>
          </div>

          <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
            <button
              onClick={() => {
                const { conjunto } = crearNombreCompleto(nombre, config);
                conjunto.updateMatrixWorld(true);
                const exporter = new STLExporter();
                const stl = exporter.parse(conjunto);
                const blob = new Blob([stl], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `nombre_completo_${nombre}_base.stl`;
                a.click();
              }}
            >
              ⬇️ Descargar STL
            </button>

            <button
              onClick={() => {
                const { conjuntoTapa } = crearNombreCompleto(nombre, config);
                conjuntoTapa.updateMatrixWorld(true);
                const exporter = new STLExporter();
                const stl = exporter.parse(conjuntoTapa);
                const blob = new Blob([stl], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `nombre_completo_${nombre}_tapas.stl`;
                a.click();
              }}
            >
              🧩 Descargar Tapas
            </button>


            
          </div>
        </>
      )}

      
    </div>
  );
}

